import { useMemo } from 'react';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { DEFAULT_UNIT_ID_DISPLAY_NAME } from 'src/common/consts/platformConfigConsts';

import { createAsyncLoadOptions, getInitialOptions } from '../../../helpers/AsyncFilterHelpers';
import { getWebSdkTestsFilterDefinitions } from '../../../helpers/WebSdkConsts';
import { getWebSdkTestsFilterDefinitionsV2 } from '../components/filter-section-v2/getWebSdkTestsFilterDefinitionsV2';

interface UseWebSdkTestsFiltersParams<T extends { labels: OptionType[] }> {
	allLabels: OptionType[];
	selectedLabels?: OptionType[] | undefined;
	filterOptions: T;
	unitIdDisplayName?: string;
	isV2?: boolean;
}

interface UseWebSdkTestsFiltersReturn<T extends { labels: OptionType[] }> {
	filterDefinitions: IFilterDefinition[];
	mergedFilterOptions: T;
}

export const useWebSdkTestsFilters = <T extends { labels: OptionType[] }>({
	allLabels,
	selectedLabels,
	filterOptions,
	unitIdDisplayName = DEFAULT_UNIT_ID_DISPLAY_NAME,
	isV2 = false
}: UseWebSdkTestsFiltersParams<T>): UseWebSdkTestsFiltersReturn<T> => {
	// Get initial options: selected + first 3 non-selected
	const initialOptions = useMemo(
		() => getInitialOptions(allLabels, selectedLabels ?? []),
		[allLabels, selectedLabels]
	);

	// Create loadOptions function that uses initialOptions when search is empty
	const loadOptions = useMemo(
		() => createAsyncLoadOptions(allLabels, initialOptions),
		[allLabels, initialOptions]
	);

	// Get filter definitions with async loading built in
	const filterDefinitions = useMemo(() => {
		if (isV2) {
			return getWebSdkTestsFilterDefinitionsV2(unitIdDisplayName, loadOptions, initialOptions);
		}
		return getWebSdkTestsFilterDefinitions(loadOptions, initialOptions);
	}, [isV2, unitIdDisplayName, loadOptions, initialOptions]);

	const mergedFilterOptions = useMemo(
		() => ({
			...filterOptions,
			labels: initialOptions
		}),
		[filterOptions, initialOptions]
	);

	return {
		filterDefinitions,
		mergedFilterOptions
	};
};
